import React, { Suspense, lazy } from 'react';
import { Router, Route, Switch } from 'react-router-dom';
import { createBrowserHistory } from 'history';
import { SnackbarProvider } from 'notistack';
import { MuiThemeProvider, StylesProvider, createGenerateClassName } from '@material-ui/core/styles';

import httpService from './services/httpService';
import muiTrimbleTheme from './assets/theme/muiTrimbleTheme';
import Notifier from './snackbars/notifier';
import ErrorBoundary from './components/errors/errorBoundary';
import LoadingSpinner from './components/loaders/combinedHeaderAndSpinner';
import Login from './components/pages/authentication/login';
import Callback from './components/pages/authentication/callback';
import Authorize from './components/pages/authentication/authorize';
import Revoke from './components/pages/authentication/revoke';
import NotAuthorized from './components/errors/notAuthorized';
import PrivateRoutes from './routes/privateRoutes';
import { publicRoutes } from './routes/appRouteList';

// Route-based code splitting
const PageNotFound = lazy((): Promise<any> => { return import('./components/errors/pageNotFound'); });

// prefix prod class names with tcxmi (Trimble Cloud eXchange Transportation Insights)
const generateClassName = createGenerateClassName({ productionPrefix: 'tcxti' });

// create browser history for the axios interceptor to use and then initialize the interceptors
const history = createBrowserHistory();
httpService.setupInterceptors(history);

const App: React.FC = (): JSX.Element => {
    return (
        <StylesProvider generateClassName={generateClassName}>
            <MuiThemeProvider theme={muiTrimbleTheme}>
                <SnackbarProvider maxSnack={3}>
                    <Notifier />
                    <Router history={history}>
                        <Suspense fallback={<LoadingSpinner />}>
                            <Switch>
                                <Route path={publicRoutes.root} exact>
                                    <ErrorBoundary>
                                        <Login />
                                    </ErrorBoundary>
                                </Route>
                                <Route path={publicRoutes.callback}>
                                    <ErrorBoundary>
                                        <Callback />
                                    </ErrorBoundary>
                                </Route>
                                <Route path={publicRoutes.authorize}>
                                    <ErrorBoundary>
                                        <Authorize />
                                    </ErrorBoundary>
                                </Route>
                                <Route path={publicRoutes.revoke}>
                                    <ErrorBoundary>
                                        <Revoke />
                                    </ErrorBoundary>
                                </Route>
                                <Route path={publicRoutes.notAuthorized}>
                                    <ErrorBoundary>
                                        <NotAuthorized />
                                    </ErrorBoundary>
                                </Route>
                                <PrivateRoutes />
                                <Route>
                                    <PageNotFound />
                                </Route>
                            </Switch>
                        </Suspense>
                    </Router>
                </SnackbarProvider>
            </MuiThemeProvider>
        </StylesProvider>
    );
};

export default App;
